import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, Link } from "react-router-dom";
import { Star, ShieldCheck, Leaf, Package, Award } from "lucide-react";
import api from "../lib/api";
import ShippingDisclaimer from "../components/ShippingDisclaimer";
import DHLEstimator from "../components/DHLEstimator";
import UpsellSameShipping from "../components/UpsellSameShipping";
import { useStore } from "../lib/store";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [variant, setVariant] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [qty, setQty] = useState(1);
  const { addToCart } = useStore();

  const estimateItems = useMemo(
    () => (product && variant ? [{ product_id: product.id, sku: variant.sku, qty }] : []),
    [product, variant, qty],
  );

  useEffect(() => {
    setProduct(null);
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      setVariant(r.data.variants[0]);
      api.get("/reviews", { params: { product_id: r.data.id, limit: 6 } })
        .then((rr) => setReviews(rr.data));
    });
    window.scrollTo(0, 0);
  }, [slug]);

  if (!product) {
    return <div className="container-kv pt-40 pb-20 text-center text-[#75726D]" data-testid="product-loading">Loading...</div>;
  }

  const smallest = product.variants[0];
  const showUpsell = variant?.sku !== smallest.sku;

  return (
    <>
      <Helmet>
        <title>{`${product.name} Borneo Kratom | Kratoven`}</title>
        <meta
          name="description"
          content={product.short_desc || product.description}
        />
        <link rel="canonical" href={`https://kratoven.com/product/${product.slug}`} />
      </Helmet>

      <div className="pt-28 pb-20" data-testid={`product-${product.slug}`}>
      <div className="container-kv">
        <div className="text-xs text-[#75726D] mb-6 tracking-wide">
          <Link to="/" className="hover:text-[#C8A97E]">Home</Link> ·{" "}
          <Link to="/shop" className="hover:text-[#C8A97E]">Shop</Link> ·{" "}
          <span className="text-[#A8A5A1]">{product.name}</span>
        </div>

        <div className="grid md:grid-cols-2 gap-10 lg:gap-16">
          <div className="product-img-box">
            <img src={product.image} alt={product.name} loading="eager" decoding="async" width="800" height="800" />
          </div>

          <div>
            <div className="overline mb-3">{product.strain.replace("-", " ")} Borneo</div>
            <h1 className="font-serif text-4xl lg:text-5xl tracking-tighter font-light mb-4">
              {product.name}
            </h1>

            <div className="flex items-center gap-3 mb-6">
              <div className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={14} className="fill-[#C8A97E] text-[#C8A97E]" />
                ))}
              </div>
              <span className="text-xs text-[#A8A5A1]">{product.rating} · {product.reviews_count} reviews</span>
            </div>

            <p className="text-[#A8A5A1] leading-relaxed mb-8">{product.description}</p>

            <div className="mb-8">
              <div className="text-xs tracking-widest uppercase text-[#75726D] mb-3">Select Weight</div>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="variant-selector">
                {product.variants.map((v) => (
                  <button
                    key={v.sku}
                    onClick={() => setVariant(v)}
                    className={`variant-box ${variant?.sku === v.sku ? "selected" : ""}`}
                    data-testid={`variant-${v.sku}`}
                  >
                    <div className="font-medium">{v.weight}</div>
                    <div className="text-xs mt-1 opacity-80">${v.price.toFixed(2)}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-baseline gap-4 mb-6">
              <span className="font-serif text-4xl text-[#C8A97E]">${variant?.price.toFixed(2)}</span>
              <span className="text-sm text-[#75726D]">{variant?.weight} · Resealable foil pouch</span>
            </div>

            <div className="flex items-stretch gap-3 mb-5">
              <div className="flex items-center border border-white/15">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 text-[#A8A5A1] hover:text-white" data-testid="qty-dec">−</button>
                <span className="px-4 text-sm" data-testid="qty-display">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="px-4 text-[#A8A5A1] hover:text-white" data-testid="qty-inc">+</button>
              </div>
              <button
                onClick={() => addToCart(product, variant, qty)}
                className="btn-gold flex-1"
                data-testid="add-to-cart-btn"
              >
                Add to Cart · ${(variant.price * qty).toFixed(2)}
              </button>
            </div>

            {showUpsell && (
              <div className="mb-5">
                <UpsellSameShipping
                  smallestVariant={smallest}
                  onAdd={(v) => addToCart(product, v, 1)}
                />
              </div>
            )}

            <div className="mb-6">
              <DHLEstimator items={estimateItems} />
            </div>

            <div className="grid grid-cols-2 gap-3 mb-8">
              <Badge icon={ShieldCheck} label="Lab-Tested" />
              <Badge icon={Leaf} label="100% Pure" />
              <Badge icon={Award} label="45-Day Guarantee" />
              <Badge icon={Package} label="DHL Express" />
            </div>

            <ShippingDisclaimer />

            <div className="mt-8">
              <div className="text-xs tracking-widest uppercase text-[#75726D] mb-3">Reported Effects</div>
              <div className="flex flex-wrap gap-2">
                {product.effects.map((e) => (
                  <span key={e} className="px-3 py-1 border border-[#C8A97E]/30 text-[#C8A97E] text-xs tracking-wide">
                    {e}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {reviews.length > 0 && (
          <section className="mt-24" data-testid="product-reviews">
            <div className="overline mb-3">Verified Reviews</div>
            <h2 className="font-serif text-3xl font-light mb-10">What customers are saying</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.map((r) => (
                <div key={r.id} className="card-kv">
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: r.rating }).map((_, i) => (
                      <Star key={i} size={12} className="fill-[#C8A97E] text-[#C8A97E]" />
                    ))}
                  </div>
                  <p className="text-sm text-[#A8A5A1] leading-relaxed mb-4">"{r.text}"</p>
                  <div className="text-xs text-[#75726D] tracking-wider uppercase">{r.author} · Verified</div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
    </>
  );
}

function Badge({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 p-3 border border-white/5 bg-[#0D120E] text-xs">
      <Icon size={14} className="text-[#C8A97E]" />
      <span className="text-[#A8A5A1]">{label}</span>
    </div>
  );
}
